import { concaveBlendT } from './shapeEyes'
import { lerp } from './math'

/** Concave pocket rail strength — star ≈ 1, mild waists still get a usable arc. */
function pocketStrength(radii: number[] | null): number {
  const t = concaveBlendT(radii)
  if (t <= 0) return 0
  return lerp(0.42, 1, t)
}

/** Sizes the pocket ellipse — mirrors `followTangentMax` in `src/ui/gaze.ts`. */
function tangentMax(radii: number[] | null): { x: number; y: number } {
  const t = concaveBlendT(radii)
  const yaw = lerp(38, 22, t)
  const pitch = lerp(30, 16, t)
  const k = (Math.PI / 180) * 0.82
  return { x: yaw * k, y: pitch * k }
}

/**
 * Per-eye slide on the screen-aligned pocket ellipse (ball-radius units). Each eye
 * sits at a different θ on the bottom arc. Height turn lifts the near-side eye
 * (look-right → right high, look-left → left high) — same rule as REST's `\\`.
 */
export function pocketEyeSlide(
  eye: 0 | 1,
  nx: number,
  ny: number,
  radii: number[] | null
): { dx: number; dy: number } {
  const max = tangentMax(radii)
  const linear = { dx: nx * max.x, dy: ny * max.y }
  const t = pocketStrength(radii)
  if (t <= 0) return linear

  const blend = concaveBlendT(radii)
  const a = max.x * 1.12
  const b = max.y * 0.55
  const alpha = (Math.PI / 180) * lerp(34, 26, blend)
  // Widen inter-eye arc when looking down — perspective on the waist pocket.
  const spread = lerp(0.18, 0.22, blend) + Math.max(0, ny) * lerp(0, 0.11, t)
  const side = eye === 0 ? -1 : 1
  const theta0 = Math.PI / 2 + side * spread
  const thetaX = theta0 - nx * alpha
  const thetaH = theta0 - Math.abs(nx) * alpha
  const near = (nx < 0 && eye === 0) || (nx > 0 && eye === 1)
  const climb = lerp(0, b * 0.42, t) * Math.abs(nx)
  const arc = {
    dx: a * (Math.cos(thetaX) - Math.cos(theta0)),
    dy:
      b * (Math.sin(thetaH) - Math.sin(theta0)) +
      ny * max.y * lerp(0.4, -0.22, t) +
      (near ? -climb : climb)
  }
  return {
    dx: lerp(linear.dx, arc.dx, t),
    dy: lerp(linear.dy, arc.dy, t)
  }
}

/**
 * Roll along the pocket. Look-right keeps REST's `\\` (right eye high). Look-left
 * on a star is flipped in the engine; on other waists a mild ease still applies.
 * Never counter-rotate nx>0 toward left-high — that melted the near-eye rule.
 */
export function pocketRollLift(
  nx: number,
  radii: number[] | null,
  baseRoll = 0
): number {
  const t = pocketStrength(radii)
  if (t <= 0 || nx <= 0) return 0
  // Soften only — do not cross zero and invert the pair.
  const ease = Math.min(lerp(0, 5, t) * nx, Math.max(0, -baseRoll) * 0.35)
  return ease
}

/** Matrix spread multiplier — eyes sit farther apart when the gaze rides low on the pocket. */
export function pocketSpreadScale(ny: number, radii: number[] | null): number {
  const t = pocketStrength(radii)
  if (t <= 0 || ny <= 0) return 1
  return 1 + ny * lerp(0, 0.065, t)
}

/**
 * Pocket rail with the pair's midpoint removed. Sphere `mix` carries the look;
 * this only keeps the waist arc (near eye climbs, pair widens on ny>0).
 */
export function pocketRailDelta(
  eye: 0 | 1,
  nx: number,
  ny: number,
  radii: number[] | null
): { dx: number; dy: number } {
  const a = pocketEyeSlide(0, nx, ny, radii)
  const b = pocketEyeSlide(1, nx, ny, radii)
  const self = eye === 0 ? a : b
  return { dx: self.dx - (a.dx + b.dx) / 2, dy: self.dy - (a.dy + b.dy) / 2 }
}

/**
 * Tiny vertical bias so the near-side eye stays above the far one on a sparkle.
 * Only rescues look-left + look-up: pitch-up flattens the roll flip there. Look-
 * right and pure left already land near REST's |dy| from roll alone — a large
 * lift stretched the pair into a diagonal (far eye dropped ~20 px).
 *
 * Returns dy in ball-radius units (negative = up on screen).
 */
export function starNearHigherLift(
  eye: 0 | 1,
  nx: number,
  ny: number,
  radii: number[] | null
): number {
  const t = pocketStrength(radii)
  if (t <= 0 || nx >= -0.08) return 0
  const up = Math.max(0, -ny)
  if (up < 0.12) return 0
  const amp = lerp(0.01, 0.026, t) * (-nx) * up
  return eye === 0 ? -amp : amp
}
